import {Component,ElementRef,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {Saml,SamlService} from "@components/admin/parametre/authentification/saml/saml.service";
import {ModeSAML,SamlConfiguration} from "@domain/admin/parametre/authentification/saml-configuration";
import {TranslateService} from "@ngx-translate/core";
import {NgForm} from "@angular/forms";
import {BehaviorSubject,Subject,takeUntil} from "rxjs";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {filter} from "rxjs/operators";

/**
 * Composant d'affichage de la page Saml
 * Ce composant permet de configurer les paramètres d'authentification SAML
 * pour l'application, incluant le mode d'authentification (IDP ou SP),
 * les claims, et les métadonnées.
 *
 * @author Lionel Gimenez
 * @date 29/07/2025
 */
@Component({
    host: {'data-test-id': 'saml'},
    selector: 'saml',
    templateUrl: './saml.component.html',
    providers: [SamlService]
})
export class SamlComponent implements OnInit,OnDestroy {
    /** Déclaration pour le template */
    ClaimType = ClaimType;
    MetadonneesType = MetadonneesType;

    /** Subject pour gérer la désinscription des observables lors de la destruction du composant */
    private destroy$ = new Subject<void>();

    /**
     * Liste des options pour le mode d'authentification SAML
     */
    modeSelectOptions: Array<{ type: ModeSAML,libelle: string }> = [
        {
            type: ModeSAML.IDP,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.modeOption.idpInitiated")
        },
        {
            type: ModeSAML.SP,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.modeOption.spInitiated")
        }
    ];

    /** Type de claim sélectionné par défaut */
    claimSelect: ClaimType = ClaimType.NAMEID;

    /**
     * Liste des options pour le claim
     */
    claimSelectOptions: Array<{ type: ClaimType,libelle: string }> = [
        {
            type: ClaimType.PERSONNALISE,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.defaultClaimOption.personnalise")
        },
        {
            type: ClaimType.NAMEID,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.defaultClaimOption.nameId")
        }
    ];

    /**
     * Liste des options pour le contexte d'authentification
     * Chargée dynamiquement à partir des données du service
     */
    contextSelectOption: Array<{ type: string,libelle: string }> = []

    /** Type de métadonnées sélectionné par défaut */
    metadonneesSelect: MetadonneesType = MetadonneesType.URL;

    /** Configuration SAML */
    config: SamlConfiguration

    /**
     * Liste des options pour les métadonnées
     */
    metadonneesSelectOptions: Array<{ type: MetadonneesType,libelle: string }> = [
        {
            type: MetadonneesType.URL,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.contexteOption.url")
        },
        {
            type: MetadonneesType.FICHIER,
            libelle: this.translateService.instant("admin.parametres.authentification.saml.contexteOption.fichier")
        }
    ];

    /**
     * Liste des actions possibles dans l'interface
     * Utilisée pour configurer les boutons flottants d'action
     */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Référence au formulaire pour la validation */
    @ViewChild('form') form: NgForm;

    /** Référence à l'input de fichier */
    @ViewChild('idpFileInput') idpFileInput: ElementRef;

    /** Fichier IDP sélectionné */
    idpFile: File = null;

    /**
     * Constructeur du composant SAML
     *
     * @param samlService - Service gérant les opérations SAML
     * @param translateService - Service de traduction pour l'internationalisation
     */
    constructor(
        public samlService: SamlService,
        private translateService: TranslateService,
    ) {
    }

    /**
     * Hook d'initialisation du composant
     * Récupère la configuration SAML existante et initialise les actions
     */
    ngOnInit(): void {
        //On récupère le modèle
        this.samlService.get()
            .pipe(
                takeUntil(this.destroy$),
                filter((saml: Saml) => saml != null))
            .subscribe((saml: Saml) => {
                this.config = saml.config;

                //Si on a une valeur sur le defaultClaim
                if (saml.config?.defaultClaim == null || saml?.config?.defaultClaim !== "") {
                    //On change le type de claim
                    this.claimSelect = ClaimType.PERSONNALISE;
                }

                //Si on a un fichier
                if (saml.config?.hasIdpFile) {
                    //On change le type de métadonnées
                    this.metadonneesSelect = MetadonneesType.FICHIER;
                }

                //On récupère la liste pour le select
                this.loadContextSelectionOption(saml.listeAuthContext);
            });

        //Initialisation des actions possibles
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.creer',
            doAction: () => this.samlService.save(this.idpFile),
            isDisabled: () => !this.form?.valid || this.isCertificatError(this.config) || this.isClefPriveeError(this.config)
        },{
            type: TypeAction.SECONDARY,
            icone: 'material-icons-outlined',
            iconeName: 'list_alt',
            libelle: 'admin.parametres.authentification.saml.accesMetadonnees',
            doAction: () => this.samlService.openMetadonnees(),
        }]);
    }

    /**
     * Vérifie si le certificat est en erreur
     * Un certificat est requis en mode SP (Service Provider)
     *
     * @param config La configuration SAML à vérifier
     * @returns true si le certificat est manquant ou vide en mode SP
     */
    isCertificatError(config: SamlConfiguration): boolean {
        //En mode SP (Service Provider), un certificat valide est obligatoire
        return config == null
            || (config.mode === ModeSAML.SP && (!config.spCertificate || config.spCertificate === ""));
    }

    /**
     * Indique si la clef privée est en erreur
     * Une clef privée est requise en mode SP (Service Provider)
     *
     * @param config La configuration SAML à vérifier
     * @returns true si la clef privée est manquante ou vide en mode SP
     */
    isClefPriveeError(config: SamlConfiguration): boolean {
        return config == null
            || (config.mode === ModeSAML.SP && (!config.spPrivateKey || config.spPrivateKey === "") && !config.hasSpPrivateKey);
    }

    /**
     * Hook de destruction du composant
     * Nettoie les souscriptions pour éviter les fuites mémoire
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Gestionnaire de changement du type de claim par défaut.
     * Si l'utilisateur sélectionne NameId, le claim personnalisé n'est plus applicable
     * et on vide donc `defaultClaim` pour éviter d'envoyer une valeur incohérente.
     *
     * @param claimType Type de claim sélectionné
     * @param config Configuration SAML courante
     */
    onClaimSelectChange(claimType: ClaimType, config: SamlConfiguration): void {
        //Si on sélectionne NameId, alors on vide le claim personnalisé
        if (claimType === ClaimType.NAMEID && config) {
            config.defaultClaim = '';
        }
    }


    /**
     * Charge la liste des options de sélection pour le contexte d'authentification
     * Traite les valeurs reçues pour extraire les libellés et trie la liste
     *
     * @param listeAuthContext - Liste des contextes d'authentification disponibles
     */
    loadContextSelectionOption(listeAuthContext: string[]) {
        this.contextSelectOption = [];

        listeAuthContext.forEach(authContext => {
            //On récupère le libellé en extrayant la partie après "classes:"
            //Format attendu: "urn:oasis:names:tc:SAML:2.0:ac:classes:Password"
            const libelle = authContext.split("classes:")[1];

            //On crée l'option avec le type (valeur complète) et le libellé (partie extraite)
            //Exemple: type="urn:oasis:names:tc:SAML:2.0:ac:classes:Password", libelle="Password"
            this.contextSelectOption.push({
                type: authContext,
                libelle: libelle
            });

            //On trie la liste par ordre alphabétique des libellés
            //Cela permet d'avoir une liste ordonnée dans le sélecteur
            this.contextSelectOption.sort((a,b) => {
                //Si le libellé de a est vide, il doit être placé en premier
                if (!a.libelle) return -1;
                //Si le libellé de b est vide, il doit être placé en premier
                if (!b.libelle) return 1;
                //Sinon, on trie par ordre alphabétique
                return a.libelle.localeCompare(b.libelle);
            });
        });
    }

    /**
     * Gestionnaire d'événement pour le clic sur le certificat
     * Ouvre une boîte de dialogue pour gérer le certificat
     */
    onCertificatClick(): void {
        this.samlService.openCertificatDialog();
    }

    /**
     * Gestionnaire d'événement pour la sélection d'un fichier
     * Stocke le fichier sélectionné pour l'envoi ultérieur
     *
     * @param event L'événement de changement du champ de fichier
     */
    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.idpFile = input.files[0];
        }
    }

    /**
     * Gestionnaire d'événement pour le clic sur la clé privée
     * Ouvre une boîte de dialogue pour gérer la clé privée
     */
    onClefPriveeClick(): void {
        this.samlService.openClefPriveeDialog();
    }
}

/**
 * Enumération des types de claim disponibles
 * - PERSONNALISE: Utilisation d'un claim personnalisé défini par l'utilisateur
 * - NAMEID: Utilisation du NameID standard fourni par le fournisseur d'identité
 */
enum ClaimType {
    PERSONNALISE,
    NAMEID,
}

/**
 * Enumération des types de métadonnées
 * - URL: Les métadonnées sont récupérées depuis une URL
 * - FICHIER: Les métadonnées sont fournies via un fichier local
 */
enum MetadonneesType {
    URL,
    FICHIER
}